import type { Configuration as MsalConfiguration } from '@azure/msal-browser'
import {
  APPLICATION_NAME,
  AuthenticationProvider,
  MsalProvider,
  type OpenApiType,
  useGetMsalConfig,
} from '@festina-life/authentication'
import {
  type Currency,
  getObjectKeys,
  type I18nMessages,
  type Language,
  mergeTranslations,
} from '@festina-life/core'
import type { Logo } from '@festina-life/ui/structure'
import { UiProvider, useThemeContext } from '@festina-life/ui/theme'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { type ComponentProps, type PropsWithChildren, Suspense } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { IntlProvider } from 'react-intl'
import { BrowserRouter } from 'react-router'

import { useIntlErrorHandler } from '../../hooks/useIntlErrorHandler'
import { BuildInfo } from '../buildInfo/BuildInfo'
import { ActionsProvider } from '../commandActions/ActionsProvider'
import { DevTools } from '../devTools/DevTools'
import { LifeErrorFallback } from '../errorFallback/LifeErrorFallback'
import { SensitiveDataMode } from '../sensitiveDataMode/SensitiveDataMode'
import { SettingsProvider } from '../settingsProvider/SettingsProvider'
import { SetPersona } from './SetPersona'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - since we invalidate manually it's OK to have a longer stale time
      retry: 2,
      retryDelay: 50,
    },
  },
})

export type AppProviderProps<AvailableLanguages extends Language> =
  PropsWithChildren<{
    build: {
      buildNo: string
      buildTime?: string
    }
    i18nMessages: I18nMessages<AvailableLanguages>
    defaultLanguage: NoInfer<AvailableLanguages>
    openApi: OpenApiType
    /**
     * Currency being used by the app.
     */
    currency: Currency
    msalConfig?: Partial<MsalConfiguration>
    shouldAllowToChangeTime?: boolean
    shouldShowTimeSettings?: boolean
    devToolsClassName?: string
  }> &
    ComponentProps<typeof Logo>

// oxlint-disable-next-line no-unused-expressions
APPLICATION_NAME // Ensure we initially get the app name before document.title is updated to be something else

export const AppProvider = <AvailableLanguages extends Language>(
  props: AppProviderProps<AvailableLanguages>,
) => (
  <QueryClientProvider client={queryClient}>
    <AppProviderInternal {...props} />
  </QueryClientProvider>
)

/**
 * Root provider used by Festina Apps. Will mount all necessary subproviders to
 * allow apps to immediately define their own routes, thereby taking care of authentication
 * error handling. This provider assumes that:
 *  - React Router Dom is used for routing
 *  - Tanstack Query is used for data fetching
 *  - Msal is used for authentication
 *  - React-intl is being used for i18n
 *  - Apps support suspense
 *
 *  If any of these aren't valid assumptions for a customer app, a different AppProvider
 *  can be implemented.
 */
const AppProviderInternal = <AvailableLanguages extends Language>({
  children,
  build: { buildNo, buildTime },
  i18nMessages,
  openApi,
  currency,
  defaultLanguage,
  msalConfig,
  images,
  shouldAllowToChangeTime = false,
  shouldShowTimeSettings = true,
  devToolsClassName,
}: AppProviderProps<AvailableLanguages>) => {
  const config = useGetMsalConfig(msalConfig)
  const intlErrorHandler = useIntlErrorHandler()
  useThemeContext() // Ensure dark mode is initialized when the app starts

  const availableLanguages = getObjectKeys(i18nMessages)

  return (
    <>
      <SensitiveDataMode />
      {config && (
        <AuthenticationProvider>
          {({ environmentSettings }) =>
            !!environmentSettings && (
              <MsalProvider
                msalInstance={config}
                environmentSettings={environmentSettings}
                openApi={openApi}
                images={images}
              >
                {(authState) => (
                  <SettingsProvider
                    authState={authState}
                    defaultLanguage={defaultLanguage}
                    currency={currency}
                    availableLanguages={availableLanguages}
                  >
                    {({ language, isInDebugMode }) => (
                      <IntlProvider
                        locale={language}
                        messages={mergeTranslations({
                          translations:
                            // Type cast here to remove the need for making SettingsProvider generic
                            i18nMessages[language as AvailableLanguages],
                          fallback: i18nMessages['en-GB'],
                        })}
                        onError={intlErrorHandler}
                      >
                        <UiProvider isInDebugMode={isInDebugMode}>
                          <ErrorBoundary FallbackComponent={LifeErrorFallback}>
                            <Suspense fallback={null}>
                              <SetPersona />
                              <ActionsProvider>
                                <BrowserRouter>{children}</BrowserRouter>
                              </ActionsProvider>
                              {environmentSettings.REACT_APP_TEST_MODE ? (
                                <DevTools
                                  shouldAllowToChangeTime={
                                    shouldAllowToChangeTime
                                  }
                                  shouldShowTimeSettings={
                                    shouldShowTimeSettings
                                  }
                                  className={devToolsClassName}
                                >
                                  <BuildInfo
                                    buildNo={buildNo}
                                    buildTime={buildTime}
                                    className="relative! p-1 leading-none!"
                                  />
                                </DevTools>
                              ) : (
                                <BuildInfo
                                  buildNo={buildNo}
                                  buildTime={buildTime}
                                  className="right-4"
                                />
                              )}
                            </Suspense>
                          </ErrorBoundary>
                        </UiProvider>
                      </IntlProvider>
                    )}
                  </SettingsProvider>
                )}
              </MsalProvider>
            )
          }
        </AuthenticationProvider>
      )}
    </>
  )
}
